import { useCallback, useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';

import httpInstances from '@/api/axiosInstance';
import Loader from '@/components/Loader';
import { FactsheetDetails } from '@/pages/Facthsheets/constants';
import { parseBackendData } from '@/utils/parseBackendData';

import FactsheetBody from './FactsheetBody';
import FactsheetHeader from './FactsheetHeader';

const FactsheetWindow = ({
  factsheetKey,
  openNewTab,
  updateTabName
}: {
  factsheetKey: string;
  openNewTab: (code: string) => void;
  updateTabName: (code: string, newName: string) => void;
}) => {
  const [factsheetDetails, setFactsheetDetails] = useState<FactsheetDetails | null>(null);
  const [error, setError] = useState('');

  const getFactsheet = useCallback(
    async (code: string): Promise<FactsheetDetails[]> => {
      return httpInstances.axiosInternalInstance
        .get(`/factsheets/details?code=${encodeURIComponent(code)}`)
        .then(response => parseBackendData(response.data))
        .catch(error => {
          console.error('Error fetching factsheetDetails: ', error);
          updateTabName(factsheetKey, code);
          setError(error);
        });
    },
    [factsheetKey, updateTabName]
  );

  useEffect(() => {
    if (factsheetKey.length > 0) {
      getFactsheet(factsheetKey).then(factsheets => {
        if (factsheets?.length > 0) {
          const details = factsheets[0];
          setFactsheetDetails(details);
          updateTabName(factsheetKey, details.code);
        }
      });
    }
  }, [factsheetKey, getFactsheet, updateTabName]);

  if (error) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        <Typography sx={{ fontSize: 30, fontWeight: 700, mb: 1, mt: 3 }}>
          Factsheet not found
        </Typography>
        <Typography sx={{ fontSize: 24 }}>We couldn’t find a factsheet with that code.</Typography>
      </Box>
    );
  }

  if (!factsheetDetails) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <Loader />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flexGrow: 1,
        overflow: 'hidden',
        height: '100%',
        width: '100%'
      }}
    >
      <FactsheetHeader factsheet={factsheetDetails} />
      <FactsheetBody
        factsheet={factsheetDetails}
        openNewTab={openNewTab}
      />
    </Box>
  );
};
export default FactsheetWindow;
